import { useMemo } from 'react';
import { getParcelRef, type ParcelRecord } from '@/utils/parcelGeometry';

/** All parcels sharing one normalized street name. */
export interface StreetGroup {
  /** Normalized street name — grouping/lookup key. */
  key: string;
  /** Street name as captured in the cadastral data, for display. */
  displayName: string;
  /** Suburb/township of the group's first parcel, for disambiguation. */
  suburb: string | null;
  entries: Array<{ houseNo: string | null; parcel: ParcelRecord }>;
}

export interface AddressIndex {
  groups: StreetGroup[];
  byKey: Map<string, StreetGroup>;
  /** Exact LISKEY/ParcelNo → parcel, for ref passthrough search. */
  refIndex: Map<string, ParcelRecord>;
  /**
   * Every string a query can match against, official street names AND
   * admin-curated aliases alike — each pointing at the `StreetGroup` it
   * should resolve to. Searching this instead of `groups` directly is what
   * makes an old/informal name (e.g. "Kerk Straat") surface the same
   * results as the official name (e.g. "Church Street").
   */
  searchNames: Array<{ key: string; group: StreetGroup }>;
}

/** The subset of a street-alias API record `buildAddressIndex` needs. */
export interface StreetAliasLike {
  alias?: string | null;
  canonical_street_name?: string | null;
  /** LISKEYs of an unnamed road's parcels — the alternative to
   *  `canonical_street_name` (never both, see the StreetAlias model). */
  parcel_refs?: string[] | null;
}

export interface AddressSearchMatch {
  group: StreetGroup;
  /** Lower tier = stronger match. */
  tier: number;
  /** House number parsed off the query, if any — narrows `group.entries`. */
  houseNoFilter: string | null;
}

const normalize = (s: string): string => s.trim().toLowerCase().replace(/\s+/g, ' ');

const EMPTY_INDEX: AddressIndex = {
  groups: [],
  byKey: new Map(),
  refIndex: new Map(),
  searchNames: [],
};

/**
 * Groups a tenant's parcels by normalized `StreetName` and indexes them for
 * search. Parcels with a blank/null `StreetName` (unnamed gravel roads) are
 * excluded — they only become searchable once tagged via the street-aliases
 * admin tool.
 *
 * `aliases` (optional) merges in admin-curated names, covering both cases
 * the raw parcel data can't express:
 *   - `canonical_street_name` set: an old/informal name for a street that
 *     already exists in this tenant's parcel data — becomes an additional
 *     searchable name pointing at that same group. Resolved dynamically
 *     here (not baked in at write time) so it stays correct across parcel
 *     reindexes. Dropped silently if no matching street is found (typo, or
 *     the street was renamed since) — there's nothing sensible to point to.
 *   - `parcel_refs` set: a custom name for a cluster of unnamed parcels —
 *     becomes its own synthetic group, resolved by looking up each ref
 *     against this same parcel array. Skipped if none of the refs resolve,
 *     or if the name collides with a real official street name.
 *
 * Cheap enough to rebuild on every parcel-array change: a tenant's parcel
 * count is bounded by its FPA boundary (hundreds to low-thousands), not the
 * ~700k-parcel municipal dataset.
 */
export function buildAddressIndex(
  parcels: ParcelRecord[],
  aliases: StreetAliasLike[] = [],
): AddressIndex {
  if (parcels.length === 0) return EMPTY_INDEX;

  const byKey = new Map<string, StreetGroup>();
  const refIndex = new Map<string, ParcelRecord>();

  for (const parcel of parcels) {
    const ref = getParcelRef(parcel);
    if (ref) refIndex.set(ref, parcel);
    if (parcel.ParcelNo) refIndex.set(parcel.ParcelNo, parcel);

    const rawName = parcel.StreetName;
    if (!rawName || !rawName.trim()) continue;

    const key = normalize(rawName);
    let group = byKey.get(key);
    if (!group) {
      group = {
        key,
        displayName: rawName.trim(),
        suburb: parcel.TownshipFarmName?.trim() || null,
        entries: [],
      };
      byKey.set(key, group);
    }
    group.entries.push({ houseNo: parcel.StreetNo?.trim() || null, parcel });
  }

  const groups = Array.from(byKey.values());
  const searchNames = groups.map((group) => ({ key: group.key, group }));
  for (const a of aliases) {
    const aliasText = a.alias?.trim();
    if (!aliasText) continue;
    const aliasKey = normalize(aliasText);

    const canonical = a.canonical_street_name?.trim();
    if (canonical) {
      const group = byKey.get(normalize(canonical));
      if (group) searchNames.push({ key: aliasKey, group });
      continue;
    }

    if (a.parcel_refs && a.parcel_refs.length > 0) {
      if (byKey.has(aliasKey)) continue; // don't shadow a real official street name
      const entries = a.parcel_refs
        .map((ref) => refIndex.get(ref))
        .filter((p): p is ParcelRecord => Boolean(p))
        .map((parcel) => ({ houseNo: parcel.StreetNo?.trim() || null, parcel }));
      if (entries.length === 0) continue;
      const group: StreetGroup = {
        key: aliasKey,
        displayName: aliasText,
        suburb: entries[0]?.parcel.TownshipFarmName?.trim() || null,
        entries,
      };
      byKey.set(aliasKey, group);
      groups.push(group);
      searchNames.push({ key: aliasKey, group });
    }
  }

  return { groups, byKey, refIndex, searchNames };
}

/** Splits "12 church street" into a leading house number + the rest. */
function parseQuery(raw: string): { houseNo: string | null; text: string } {
  const trimmed = raw.trim();
  const m = trimmed.match(/^(\d+[a-zA-Z]?)\s+(.+)$/);
  if (m) return { houseNo: m[1]!, text: m[2]! };
  return { houseNo: null, text: trimmed };
}

/**
 * Bounded Levenshtein distance check — true once `a` can reach `b` (or vice
 * versa) within `max` edits. Row-min pruning keeps this cheap even scanned
 * across every distinct street name in a tenant.
 */
function levenshteinWithin(a: string, b: string, max: number): boolean {
  if (Math.abs(a.length - b.length) > max) return false;
  const dp = new Array<number>(b.length + 1);
  for (let j = 0; j <= b.length; j++) dp[j] = j;
  for (let i = 1; i <= a.length; i++) {
    let prev = dp[0]!;
    dp[0] = i;
    let rowMin = dp[0];
    for (let j = 1; j <= b.length; j++) {
      const tmp = dp[j]!;
      dp[j] = a[i - 1] === b[j - 1] ? prev : 1 + Math.min(prev, dp[j]!, dp[j - 1]!);
      prev = tmp;
      if (dp[j]! < rowMin) rowMin = dp[j]!;
    }
    if (rowMin > max) return false;
  }
  return dp[b.length]! <= max;
}

/**
 * Scored street search over a pre-built `AddressIndex`. Tiers, strongest
 * first: exact name → prefix → substring → suburb match → exact ref
 * passthrough → (only when nothing else matched) a bounded-typo fallback.
 *
 * No fuzzy-search dependency — see the plan doc: "Church Street" vs "Kerk
 * Straat" isn't a typo a fuzzy matcher would bridge anyway; that's what the
 * street-aliases tool is for.
 */
export function searchAddressIndex(
  index: AddressIndex,
  rawQuery: string,
  limit = 20,
): AddressSearchMatch[] {
  const query = rawQuery.trim();
  if (!query) return [];

  const { houseNo, text } = parseQuery(query);
  const needle = normalize(text || query);
  if (!needle) return [];

  const results: AddressSearchMatch[] = [];
  const seen = new Set<string>();
  const push = (group: StreetGroup, tier: number) => {
    if (seen.has(group.key)) return;
    seen.add(group.key);
    results.push({ group, tier, houseNoFilter: houseNo });
  };

  for (const { key, group } of index.searchNames) if (key === needle) push(group, 1);
  for (const { key, group } of index.searchNames) if (key.startsWith(needle)) push(group, 2);
  for (const { key, group } of index.searchNames) if (key.includes(needle)) push(group, 3);
  for (const group of index.groups) {
    if (group.suburb && normalize(group.suburb).includes(needle)) push(group, 4);
  }

  // Exact LISKEY/ParcelNo passthrough — surfaces the ref's own street group.
  const refParcel = index.refIndex.get(query) ?? index.refIndex.get(query.toUpperCase());
  if (refParcel?.StreetName) {
    const group = index.byKey.get(normalize(refParcel.StreetName));
    if (group) push(group, 5);
  }

  // Typo fallback — only when the strong tiers found nothing. Checks the
  // full name AND each individual word (first-letter prefiltered), so a
  // typo'd query missing a generic suffix (e.g. "voortreker" for
  // "Voortrekker Road") still matches on the "voortrekker" word alone.
  if (results.length === 0 && needle.length >= 3) {
    const firstChar = needle[0];
    for (const { key, group } of index.searchNames) {
      const candidates = key.includes(' ') ? [key, ...key.split(' ')] : [key];
      const isMatch = candidates.some(
        (c) => c[0] === firstChar && levenshteinWithin(needle, c, 2),
      );
      if (isMatch) push(group, 6);
    }
  }

  results.sort((a, b) => a.tier - b.tier || a.group.displayName.localeCompare(b.group.displayName));
  return results.slice(0, limit);
}

/** Memoized address index — rebuilds when the parcel array or alias list changes. */
export function useAddressSearchIndex(
  parcels: ParcelRecord[],
  aliases: StreetAliasLike[] = [],
): AddressIndex {
  return useMemo(() => buildAddressIndex(parcels, aliases), [parcels, aliases]);
}
